import { createStore } from 'vuex'
import api from './../api'

export default createStore({
  state: {
    notification: {
      visible: false,
      color: 'success',
      text: ''
    },
    user: null,
    // Pääsuvõtmed Kohustuslik and this session has no key yet (/auth/status).
    mustAddPasskey: false,
    // Lekkinud paroolid: how many times this user's password is in the public
    // leak lists (/auth/status); above 0 the app reminds them to change it.
    passwordPwned: 0,
    // When the password was last checked against the leak lists; null = not yet.
    passwordCheckedAt: null,
    notices: [],
    // Which optional modules this deployment runs, e.g. { akt: true }. The menu
    // is a synchronous computed, so the answer has to live somewhere reactive.
    features: {},
    // Where this user keeps the main menu in the new frame (features.intraFrame):
    // 'left' | 'right' | 'top', and whether they have hidden it. Their own
    // choice, saved on the server — see service/userLayout.js.
    layout: { position: 'left', hidden: false },
    // This user's own settings from Minu seaded — see service/userPrefs.js.
    // calendarLook null follows Seaded (plugins/calendarLooks.js).
    prefs: { calendarLook: null, plannerLook: null, plannerMonths: null }
  },
  getters: {
    isTechnic (state) {
      return state.user && state.user.roles.some(role => ['tehnic'].includes(role))
    },
    isAdmin (state) {
      return state.user && state.user.roles.some(role => ['admin', 'root'].includes(role))
    },
    isSecretary (state) {
      return state.user && state.user.roles.some(role => ['secretary'].includes(role))
    }
  },
  mutations: {
    hideNotification (state) {
      state.notification.visible = false
    },
    showNotification (state, notification) {
      state.notification.visible = true
      state.notification.text = notification.text
      state.notification.color = notification.color || undefined
    },
    setMustAddPasskey (state, value) {
      state.mustAddPasskey = !!value
    },
    setPasswordPwned (state, count) {
      state.passwordPwned = Number(count) || 0
    },
    setPasswordCheckedAt (state, at) {
      state.passwordCheckedAt = at || null
    },
    setUser (state, user) {
      state.user = user
    },
    setNotices (state, notices) {
      state.notices = notices
    },
    setFeatures (state, features) {
      state.features = features
    },
    setLayout (state, layout) {
      state.layout = { ...state.layout, ...layout }
    },
    setPrefs (state, prefs) {
      state.prefs = { ...state.prefs, ...prefs }
    }
  },
  actions: {
    hideNotification({ commit }) {
      commit('hideNotification')
    },
    showNotification({ commit }, notification) {
      commit('showNotification', notification)
    },
    async isAuthenticated({ commit }, force = false) {
      const status = await api.getAuthStatus(force)
      // Staff and the customer portal share one session cookie, so the session
      // may belong to the other app — a customer logged in on this browser.
      // That counts as logged out here: taking it as a login let every page
      // load into 403s and bounce the reader off the site.
      const user = status.user && (status.user.roles || []).includes('employee') ? status.user : null
      commit('setUser', user)
      commit('setMustAddPasskey', !!user && status.mustAddPasskey)
      commit('setPasswordPwned', user ? status.passwordPwned : 0)
      commit('setPasswordCheckedAt', user ? status.passwordCheckedAt : null)
      return !!user
    },
    async getNotices({ commit }) {
      const notices = await api.getNotices()
      commit('setNotices', notices)
    },
    async getLayout({ commit }) {
      try {
        const { layout } = await api.getMyLayout()
        commit('setLayout', layout)
      } catch (err) {
        // The default layout stands; choosing one again saves it.
      }
    },
    // Drawn at once, then saved; a refusal puts the old layout back and says so.
    async saveLayout({ commit, state }, change) {
      const before = { ...state.layout }
      commit('setLayout', change)
      try {
        const { layout } = await api.updateMyLayout(change)
        commit('setLayout', layout)
      } catch (err) {
        commit('setLayout', before)
        commit('showNotification', { text: 'Menüü valikut ei õnnestunud salvestada: ' + err.message, color: 'red' })
      }
    },
    async getPrefs({ commit }) {
      try {
        const { prefs } = await api.getMyPrefs()
        commit('setPrefs', prefs)
      } catch (err) {
        // The Seaded defaults stand; choosing again saves.
      }
    },
    // Like saveLayout: drawn at once, put back and said so if refused.
    async savePrefs({ commit, state }, change) {
      const before = { ...state.prefs }
      commit('setPrefs', change)
      try {
        const { prefs } = await api.updateMyPrefs(change)
        commit('setPrefs', prefs)
      } catch (err) {
        commit('setPrefs', before)
        commit('showNotification', { text: 'Seadet ei õnnestunud salvestada: ' + err.message, color: 'red' })
      }
    },
    async getFeatures({ commit }) {
      try {
        commit('setFeatures', await api.getFeatures())
      } catch (err) {
        // A menu that could not ask stays as it is — a module link is not worth
        // a toast, and the next page load asks again.
      }
    }
  },
  modules: {
  }
})
